import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import {
  DateDisplayComponent,
  DateEditComponent,
  NumericEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
  TextDisplayComponent,
  TextEditComponent,
} from '@grid/components';
import { GridColumn, GridOptions, HeaderOptions } from '@grid/models';
import { TimeDisplayComponent, TimeEditComponent } from '@shared/components';
import { DataType } from '@shared/models';
import { DprWeatherDetailGridData } from '@dpr-details/models/grid/dpr-weather-detail-grid-data.model';
import { DprDetailsGridBaseComponent } from '@dpr-details/models/grid';
import {
  EditableComponent,
  RegistrationsComponent,
} from '@dpr-details/models/component';
import { GridAlignment, GridSortDirection, GridSortMode } from '@grid/enums';
import { selectTooltipMessage } from '@grid/utils/select';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { Constants as GridConstants } from '@grid/utils/constants';
import { Constants, buildBooleanValues, getStaticMessage } from '@shared/utils';
import { StaticMessage } from '@shared/enums';
import { BooleanTerminologyType } from '@shared/enums/boolean-terminology';

@UntilDestroy()
@Component({
  selector: 'app-dpr-weather-registrations-grid',
  templateUrl: './dpr-weather-registrations-grid.component.html',
  styleUrls: ['./dpr-weather-registrations-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprWeatherRegistrationsGridComponent
  extends DprDetailsGridBaseComponent<DprWeatherDetailGridData>
  implements
    RegistrationsComponent<DprWeatherDetailGridData>,
    EditableComponent,
    OnChanges
{
  @ViewChild('timeComponent', { static: true })
  timeComponent: TemplateRef<any>;

  private get weatherColumns(): GridColumn<DprWeatherDetailGridData>[] {
    const columns: GridColumn<any>[] = [];

    // Necessary at least one fluid column in table
    if (!this.projectSetup?.weatherList?.length) {
      columns.push({
        columnDef: 'spacerColumn',
        disableFocusedBackground: true,
        tabIndex: -1,
        minWidth: 10,
      });

      return columns;
    }

    this.projectSetup.weatherList.forEach((x) => {
      const header: HeaderOptions<DprWeatherDetailGridData> = {
        name: x.units ? `${x.name} [${x.units}]` : x.name,
      };

      let column: GridColumn<DprWeatherDetailGridData> = {
        columnDef: `${x.id}`,
        header: header,
        minWidth: 80,
        allowSorting: false,
      };

      switch (x.dataTypeId) {
        case DataType.FreeText: {
          column = {
            ...column,
            display: { type: TextDisplayComponent },
            edit: {
              type: TextEditComponent,
              config: {
                options: {
                  maxLength: Constants.FieldLimits.maxFreeTextDataTypeLimit,
                },
              },
            },
          };
          break;
        }
        case DataType.Number: {
          column = {
            ...column,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            display: { type: TextDisplayComponent },
            edit: {
              type: NumericEditComponent,
              config: {
                options: {
                  step: 0.01,
                  decimalPrecision: 2,
                  min: Constants.NumericOptions.minWeather,
                  max: Constants.NumericOptions.maxWeather,
                },
              },
            },
          };
          break;
        }
        case DataType.Percentage: {
          column = {
            ...column,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            display: {
              type: TextDisplayComponent,
              config: {
                options: {
                  allowPercentage: true,
                },
              },
            },
            edit: {
              type: NumericEditComponent,
              config: {
                options: {
                  step: 0.01,
                  decimalPrecision: 2,
                  allowPercentage: true,
                },
              },
            },
          };
          break;
        }
        case DataType.Date: {
          column = {
            ...column,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            minWidth: 100,
            display: { type: DateDisplayComponent },
            edit: {
              type: DateEditComponent,
              config: {
                allowClearOption: true,
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  withoutTimeZone: true,
                },
              },
              delegateEndEdit: true,
            },
          };
          break;
        }
        case DataType.Datetime: {
          column = {
            ...column,
            header: {
              ...header,
              alignment: GridAlignment.Right,
            },
            columnAlignment: GridAlignment.Right,
            minWidth: 160,
            display: {
              type: DateDisplayComponent,
              config: {
                format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
              },
            },
            edit: {
              type: DateEditComponent,
              config: {
                saveButtonLabel: getStaticMessage(StaticMessage.SaveButton),
                clearButtonLabel: getStaticMessage(StaticMessage.ClearButton),
                timeOptions: {
                  allowTimePicker: true,
                  timePickerComponent: this.timeComponent,
                  withoutTimeZone: true,
                },
                format: GridConstants.DefaultValues.dateDisplayFormatWithTime,
              },
              delegateEndEdit: true,
            },
          };
          break;
        }
        case DataType.List: {
          const options = this.projectSetup.weatherDataTypeValueList
            .filter((w) => w.weatherId === x.id)
            .map((v) => ({ id: v.id, name: v.value }));

          column = {
            ...column,
            display: {
              type: SelectDisplayComponent,
              config: { options },
            },
            edit: {
              type: SelectEditComponent,
              config: {
                options: (data: DprWeatherDetailGridData) => {
                  if (data && data[column.columnDef]) {
                    data[column.columnDef] = +data[column.columnDef];
                  }
                  return options;
                },
                includeNoneOption: true,
                noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
              },
            },
            tooltip: {
              message: selectTooltipMessage(options),
            },
          };

          break;
        }
        case DataType.Boolean: {
          const options = buildBooleanValues(
            x.booleanTerminologyId ?? BooleanTerminologyType.Default
          ).map((v) => ({ id: v.id, name: v.value }));

          column = {
            ...column,
            display: {
              type: SelectDisplayComponent,
              config: { options },
            },
            edit: {
              type: SelectEditComponent,
              config: {
                options: (data: DprWeatherDetailGridData) => {
                  if (data && data[column.columnDef]) {
                    data[column.columnDef] = +data[column.columnDef];
                  }
                  return options;
                },
                includeNoneOption: true,
                noneOptionName: getStaticMessage(StaticMessage.NoneOptionName),
              },
            },
            tooltip: {
              message: selectTooltipMessage(options),
            },
          };

          break;
        }
      }

      columns.push(column);
    });

    return columns;
  }

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridColumns$.next(this.getGridColumns());
      this.gridOptions$.next(this.getGridOptions());
    }
  }

  //#endregion

  isRowEmpty(data: DprWeatherDetailGridData): boolean {
    if (data.time != null) {
      return false;
    }

    // TODO(Improvement) create dynamic type from the columns, use that for validation
    // https://stackoverflow.com/questions/55020193/is-it-possible-to-create-a-typescript-type-from-an-array
    return this.projectSetup.weatherList
      ?.map((x) => data[x.id] == null)
      ?.every((x) => x);
  }

  protected getGridOptions(): GridOptions<DprWeatherDetailGridData> {
    return {
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: this.canEdit,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: this.canEdit,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: this.canEdit,
        allowSelection: this.canEdit,
        isEmpty: this.isRowEmpty.bind(this),
      },
      selectionConfig: {
        allowSelection: this.canEdit,
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
        definition: [
          {
            columnDef: 'time',
            direction: GridSortDirection.Ascending,
          },
        ],
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprWeatherDetailGridData>[] {
    return [
      {
        columnDef: 'time',
        header: {
          name: this.labeling?.timeFieldName,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        display: {
          type: TimeDisplayComponent,
        },
        edit: {
          type: TimeEditComponent,
          config: { options: { maxLength: 200 } },
        },
        allowSorting: false,
        width: 90,
      },
      ...this.weatherColumns,
      GridColumns.delete,
    ];
  }
}
